// @file A "View as" preview is read-only: Rails refuses every write with 403 VIEW_AS_PREVIEW_READ_ONLY
// (ViewAsPreview concern). During a preview, this swaps each failure snackbar for one "Preview only" message.
//
// It matches failure snackbars, not the 403, on purpose. A failure snackbar means a user action failed,
// so the message shows only when the admin did something. Checking the code in fetchJson would also fire
// on background writes (post heights on load, draft autosave on each edit), would miss calls that skip
// fetchJson (raw fetch, Brahms, tldraw), and would put view-as logic in a shared layer.
//
// Known limits: a real GET failure during a preview also reads "Preview only". A created post stays
// visible until refresh or persona switch; this module does not roll it back.
import { __ } from '@@/bits/intl'
import { SnackbarNotificationType } from '@@/enums'
import { useGlobalSnackbarStore, type GlobalSnackbarNotification } from '@@/pinia/global_snackbar'
import { viewAsPersonaForHeader } from '@@/surface/view_as_header_persona'
import { watch, type WatchStopHandle } from 'vue'

const FAILURE_ANALYTICS_IDS = new Set(['save_changes_failed'])

function isViewAsPreviewActive(): boolean {
  return viewAsPersonaForHeader() != null
}

function showViewAsPreviewOnlyNotice(): void {
  useGlobalSnackbarStore().setSnackbar(
    { message: __('Preview only. Nothing was saved.'), analyticsId: 'view_as_preview_only' },
    { allowDuplicate: false },
  )
}

function isFailureSnackbar(snackbar: GlobalSnackbarNotification): boolean {
  return (
    snackbar.notificationType === SnackbarNotificationType.error ||
    (snackbar.analyticsId != null && FAILURE_ANALYTICS_IDS.has(snackbar.analyticsId))
  )
}

function watchViewAsPreviewFailureSnackbars(): WatchStopHandle {
  const snackbarStore = useGlobalSnackbarStore()
  return watch(
    () => snackbarStore.snackbarArray,
    (snackbars) => {
      if (!isViewAsPreviewActive()) return
      const failures = snackbars.filter(isFailureSnackbar)
      if (failures.length === 0) return
      failures.forEach((snackbar) => snackbarStore.removeSnackbar(snackbar.uid))
      showViewAsPreviewOnlyNotice()
    },
    // Sync, so a failure snackbar is swapped before GlobalSnackbar renders it; otherwise its leave transition flashes.
    { flush: 'sync' },
  )
}

export { isViewAsPreviewActive, showViewAsPreviewOnlyNotice, watchViewAsPreviewFailureSnackbars }
